/**
 * Situation Analysis and Response Data
 * Location-aware humanitarian data for Analysis and Operations pages
 * Based on "Situation Analysis & Response Pitch" mockup
 */

import { nrcLocations, NRCRegion, regionColors } from './nrcLocations';

// Types
export interface SituationData {
  country: string;
  region: string;
  lastUpdated: string;
  executiveSummary: {
    title: string;
    crisisType: string;
    displaced: number;
    households: number;
    trend: string;
    trendDirection: 'up' | 'down' | 'stable';
    sectors: string[];
    severity: 'critical' | 'high' | 'medium';
    securityLevel: 'red' | 'amber' | 'green';
  };
  currentSituation: string[];
  populationImpact: {
    households: number;
    totalDisplaced: number;
    children: number;
    women: number;
    elderly: number;
    infrastructureStress: string;
    hostCapacity: string;
  };
  impactAssessment: Array<{
    sector: string;
    severity: 'critical' | 'high' | 'medium';
    number: number;
    unit: string;
    description: string;
  }>;
  environmentalFactors: {
    floodRisk: string;
    rainfallForecast: string;
    secondaryRisk: string;
  };
  protectionConcerns: string[];
  secondaryEffects: string[];
}

export interface ResponseData {
  nrcCapacity: {
    totalStaff: number;
    nationalStaff: number;
    internationalStaff: number;
    sinceYear: number;
    officeCount: number;
    activePrograms: number;
  };
  comparativeAdvantage: Array<{
    title: string;
    description: string;
    icon: string;
  }>;
  partnershipNetwork: {
    localNGOs: number;
    clusterRole: string;
    unAgencies: string[];
    communityNetworks: string;
  };
  responsePhases: Array<{
    phase: number;
    name: string;
    duration: string;
    activities: string[];
    budget: number;
  }>;
  timeline: Array<{
    day: number;
    milestone: string;
    target: string;
  }>;
  resourceRequirements: {
    totalBudget: number;
    budgetByPhase: Array<{ phase: string; amount: number; label: string }>;
    humanResources: {
      surge: number;
      full: number;
      international: number;
      national: number;
      specialists: string[];
    };
    materials: string[];
  };
  riskAssessment: Array<{
    risk: string;
    severity: 'high' | 'medium' | 'low';
    description: string;
    mitigation: string;
  }>;
  coordination: {
    clusters: Array<{ name: string; role: string; activities: string }>;
    unAgencies: Array<{ name: string; collaboration: string }>;
    ingos: string[];
    localPartners: Array<{ type: string; role: string }>;
    informationSharing: string[];
  };
}

// Sudan Situation Data
export const sudanSituationData: SituationData = {
  country: 'Sudan',
  region: 'Khartoum North',
  lastUpdated: '2 hours ago',
  executiveSummary: {
    title: 'Armed Conflict Displacement Crisis',
    crisisType: 'Conflict',
    displaced: 47500,
    households: 15800,
    trend: '+12%',
    trendDirection: 'up',
    sectors: ['Shelter', 'WASH', 'Protection', 'Health', 'Food Security'],
    severity: 'critical',
    securityLevel: 'red',
  },
  currentSituation: [
    'Armed clashes intensified in Khartoum North over the past 72 hours',
    'Secondary displacement from Omdurman reaching critical levels',
    'Main bridge crossing points intermittently closed',
    'Health facilities in affected areas non-functional',
    'Severe disruption to water and electricity infrastructure',
  ],
  populationImpact: {
    households: 15800,
    totalDisplaced: 47500,
    children: 18900,
    women: 14250,
    elderly: 4750,
    infrastructureStress: '78% of host community services at capacity',
    hostCapacity: 'Absorption capacity critically strained',
  },
  impactAssessment: [
    { sector: 'Shelter', severity: 'critical', number: 8500, unit: 'HH', description: 'households in critical shelter need' },
    { sector: 'WASH', severity: 'high', number: 35000, unit: 'people', description: 'without safe water access' },
    { sector: 'Protection', severity: 'critical', number: 12000, unit: 'individuals', description: 'at-risk individuals identified' },
    { sector: 'Health', severity: 'high', number: 6200, unit: 'cases', description: 'requiring medical attention' },
    { sector: 'Food Security', severity: 'medium', number: 28000, unit: 'people', description: 'food insecure' },
  ],
  environmentalFactors: {
    floodRisk: 'High-risk flood areas in 3 displacement sites',
    rainfallForecast: '85mm expected over next 14 days',
    secondaryRisk: 'Predicted flooding may cause secondary displacement of 5,000+ people',
  },
  protectionConcerns: [
    'Elevated GBV risk in overcrowded displacement sites',
    'Family separation during rapid displacement',
    '8,400 children out of school for 6+ weeks',
    'Elderly and disabled facing barriers to assistance access',
  ],
  secondaryEffects: [
    'Predicted flooding in 2 weeks may worsen conditions',
    'Market disruption causing 40% food price increase',
    'Cholera outbreak risk elevated due to WASH gaps',
  ],
};

// Ethiopia Situation Data
export const ethiopiaSituationData: SituationData = {
  country: 'Ethiopia',
  region: 'Somali Region',
  lastUpdated: '4 hours ago',
  executiveSummary: {
    title: 'Cholera Outbreak Emergency',
    crisisType: 'Disease Outbreak',
    displaced: 8500,
    households: 2400,
    trend: '+34 cases/24h',
    trendDirection: 'up',
    sectors: ['WASH', 'Health', 'Shelter', 'Food Security'],
    severity: 'critical',
    securityLevel: 'amber',
  },
  currentSituation: [
    'Confirmed cholera cases: 247 in Jijiga and surrounding woredas',
    'Case fatality rate: 2.4% (above acceptable threshold)',
    '46-hour intervention window remaining for containment',
    'Water source contamination identified at 3 key water points',
    'Health facilities overwhelmed, requesting emergency supplies',
  ],
  populationImpact: {
    households: 2400,
    totalDisplaced: 8500,
    children: 3400,
    women: 2550,
    elderly: 850,
    infrastructureStress: '3 health centers at 140% capacity',
    hostCapacity: 'Medical supplies at 32% of required levels',
  },
  impactAssessment: [
    { sector: 'WASH', severity: 'critical', number: 35000, unit: 'people', description: 'affected by water contamination' },
    { sector: 'Health', severity: 'critical', number: 247, unit: 'cases', description: 'confirmed cholera cases' },
    { sector: 'Shelter', severity: 'high', number: 1200, unit: 'HH', description: 'requiring relocation from risk zones' },
    { sector: 'Food Security', severity: 'medium', number: 8500, unit: 'people', description: 'at nutritional risk' },
  ],
  environmentalFactors: {
    floodRisk: 'Moderate - seasonal rains beginning',
    rainfallForecast: '45mm expected over next 7 days',
    secondaryRisk: 'Contaminated runoff may spread disease to neighboring woredas',
  },
  protectionConcerns: [
    'Stigmatization of affected households',
    'Children at elevated risk of dehydration',
    'Pregnant women requiring specialized care',
    'Access barriers for remote communities',
  ],
  secondaryEffects: [
    'Market closure affecting 12,000 traders',
    'School closures in 8 affected kebeles',
    'Livestock movement restrictions impacting pastoral livelihoods',
  ],
};

// Sudan Response Data
export const sudanResponseData: ResponseData = {
  nrcCapacity: {
    totalStaff: 28,
    nationalStaff: 22,
    internationalStaff: 6,
    sinceYear: 2018,
    officeCount: 3,
    activePrograms: 5,
  },
  comparativeAdvantage: [
    {
      title: 'Integrated Programming',
      description: 'Combined shelter and protection expertise enables holistic response',
      icon: 'layers',
    },
    {
      title: 'Local Relationships',
      description: 'Strong ties with local authorities and community leaders',
      icon: 'users',
    },
    {
      title: 'Pre-positioned Stock',
      description: '3,000 emergency shelter kits available for immediate deployment',
      icon: 'package',
    },
    {
      title: 'Urban Displacement Expertise',
      description: 'Specialized experience in urban displacement contexts',
      icon: 'building',
    },
  ],
  partnershipNetwork: {
    localNGOs: 3,
    clusterRole: 'Shelter/NFI Working Group Co-lead',
    unAgencies: ['UNHCR', 'IOM', 'OCHA'],
    communityNetworks: '12 community-based organizations',
  },
  responsePhases: [
    {
      phase: 1,
      name: 'Immediate Lifesaving Response',
      duration: 'Days 1-14',
      activities: [
        'Deploy emergency response team (12 staff)',
        'Distribute emergency shelter kits to 3,000 HH',
        'Establish 4 mobile protection desks',
        'Rehabilitate 2 critical water points',
      ],
      budget: 1100000,
    },
    {
      phase: 2,
      name: 'Sustained Assistance',
      duration: 'Weeks 3-8',
      activities: [
        'Scale shelter support to 8,500 households',
        'Establish women and girls safe spaces (4 locations)',
        'ICLA services for displaced populations',
        'Cash assistance pilot for 1,500 households',
      ],
      budget: 1400000,
    },
    {
      phase: 3,
      name: 'Recovery and Resilience',
      duration: 'Months 3-6',
      activities: [
        'Transition to durable shelter solutions',
        'Livelihood support for displaced and host communities',
        'Educational support for displaced children',
        'Handover to development programming',
      ],
      budget: 700000,
    },
  ],
  timeline: [
    { day: 7, milestone: 'Emergency response established', target: '1,000 HH supported' },
    { day: 30, milestone: 'Full scale-up achieved', target: '4,000 HH + protection services' },
    { day: 60, milestone: 'Cash pilot evaluation', target: '6,500 HH reached' },
    { day: 90, milestone: 'Full target achieved', target: '8,500 HH assisted' },
  ],
  resourceRequirements: {
    totalBudget: 3200000,
    budgetByPhase: [
      { phase: 'Phase 1', amount: 1100000, label: 'Emergency' },
      { phase: 'Phase 2', amount: 1400000, label: 'Scale-up' },
      { phase: 'Phase 3', amount: 700000, label: 'Recovery' },
    ],
    humanResources: {
      surge: 12,
      full: 35,
      international: 15,
      national: 20,
      specialists: ['Shelter Engineers', 'Protection Officers', 'WASH Specialists', 'Cash/Voucher Experts'],
    },
    materials: [
      '8,500 emergency shelter kits',
      '8,500 NFI packages',
      '4 WASH rehabilitation kits',
      'Safe spaces equipment (4 sets)',
      '4 additional 4x4 vehicles',
      '2 distribution trucks',
    ],
  },
  riskAssessment: [
    {
      risk: 'Security',
      severity: 'high',
      description: 'Ongoing armed conflict in operational areas',
      mitigation: 'Remote management protocols, armed escorts coordination, INSO liaison',
    },
    {
      risk: 'Access',
      severity: 'medium',
      description: 'Checkpoints and movement restrictions',
      mitigation: 'Government relations, local partner capacity, advance negotiation',
    },
    {
      risk: 'Operational',
      severity: 'medium',
      description: 'Supply chain disruption, staff safety',
      mitigation: 'Pre-positioned stocks, mobile delivery modality, staff tracking',
    },
    {
      risk: 'Environmental',
      severity: 'high',
      description: 'Flooding forecast for displacement sites',
      mitigation: 'Flood-aware site selection, drainage design, contingency planning',
    },
    {
      risk: 'Coordination',
      severity: 'low',
      description: 'Actor overlap and coverage gaps',
      mitigation: 'Active cluster participation, 3W mapping, bilateral coordination',
    },
  ],
  coordination: {
    clusters: [
      { name: 'Shelter/NFI Cluster', role: 'Co-lead', activities: '3W mapping, standards harmonization' },
      { name: 'Protection Cluster', role: 'Member', activities: 'GBV & child protection sub-clusters' },
      { name: 'WASH Cluster', role: 'Member', activities: 'Water point rehabilitation coordination' },
    ],
    unAgencies: [
      { name: 'UNHCR', collaboration: 'Shelter materials, protection referrals' },
      { name: 'IOM', collaboration: 'DTM data sharing, camp management' },
      { name: 'OCHA', collaboration: 'Strategic coordination, information management' },
      { name: 'UNICEF', collaboration: 'Child protection, education partnerships' },
    ],
    ingos: ['DRC', 'IRC', 'Save the Children', 'Mercy Corps', 'NCA', 'Plan International'],
    localPartners: [
      { type: 'Local NGOs', role: 'Community mobilization, last-mile delivery' },
      { type: 'Community leaders', role: 'Access negotiation, beneficiary selection' },
      { type: 'Government', role: 'MoU with HAC, line ministry coordination' },
    ],
    informationSharing: [
      'Daily sitreps (OCHA)',
      'Weekly cluster updates',
      'Monthly donor reports',
      'Real-time CLEAR data integration',
    ],
  },
};

// Ethiopia Response Data
export const ethiopiaResponseData: ResponseData = {
  nrcCapacity: {
    totalStaff: 42,
    nationalStaff: 35,
    internationalStaff: 7,
    sinceYear: 2011,
    officeCount: 5,
    activePrograms: 6,
  },
  comparativeAdvantage: [
    {
      title: 'WASH Expertise',
      description: 'Proven track record in emergency water supply and sanitation',
      icon: 'droplet',
    },
    {
      title: 'Rapid Response Capacity',
      description: 'Established emergency response team with cholera experience',
      icon: 'zap',
    },
    {
      title: 'Regional Presence',
      description: 'Existing operations in Somali Region with local partnerships',
      icon: 'map-pin',
    },
    {
      title: 'Health Integration',
      description: 'Experience coordinating with health authorities on outbreak response',
      icon: 'heart',
    },
  ],
  partnershipNetwork: {
    localNGOs: 4,
    clusterRole: 'WASH Cluster active member',
    unAgencies: ['WHO', 'UNICEF', 'OCHA', 'WFP'],
    communityNetworks: '8 community health worker networks',
  },
  responsePhases: [
    {
      phase: 1,
      name: 'Emergency Outbreak Response',
      duration: 'Days 1-7',
      activities: [
        'Deploy WASH emergency team (8 staff)',
        'Water point testing and treatment',
        'ORS distribution to 5,000 households',
        'Community hygiene promotion campaign',
      ],
      budget: 450000,
    },
    {
      phase: 2,
      name: 'Containment and Treatment',
      duration: 'Weeks 2-4',
      activities: [
        'Establish 3 oral rehydration points',
        'Scale up water treatment to all affected kebeles',
        'Support health facility capacity',
        'Targeted shelter for isolation cases',
      ],
      budget: 680000,
    },
    {
      phase: 3,
      name: 'Recovery and Prevention',
      duration: 'Months 2-3',
      activities: [
        'Permanent water source rehabilitation',
        'Community WASH committee training',
        'Health system strengthening',
        'Early warning system establishment',
      ],
      budget: 320000,
    },
  ],
  timeline: [
    { day: 3, milestone: 'Emergency team deployed', target: 'Water testing complete' },
    { day: 7, milestone: 'Immediate response', target: '5,000 HH with ORS' },
    { day: 14, milestone: 'Containment measures', target: 'Case rate declining' },
    { day: 30, milestone: 'Outbreak contained', target: 'Zero new cases for 7 days' },
  ],
  resourceRequirements: {
    totalBudget: 1450000,
    budgetByPhase: [
      { phase: 'Phase 1', amount: 450000, label: 'Emergency' },
      { phase: 'Phase 2', amount: 680000, label: 'Containment' },
      { phase: 'Phase 3', amount: 320000, label: 'Recovery' },
    ],
    humanResources: {
      surge: 8,
      full: 24,
      international: 8,
      national: 16,
      specialists: ['WASH Engineers', 'Public Health Officers', 'Community Mobilizers', 'Logistics Coordinators'],
    },
    materials: [
      '50,000 ORS packets',
      '10,000 hygiene kits',
      'Water treatment chemicals (3 months)',
      '5 water testing kits',
      'IEC materials (posters, leaflets)',
      '2 water trucking contracts',
    ],
  },
  riskAssessment: [
    {
      risk: 'Disease Spread',
      severity: 'high',
      description: 'Outbreak may spread to neighboring woredas',
      mitigation: 'Rapid containment, border monitoring, proactive prevention',
    },
    {
      risk: 'Supply Chain',
      severity: 'medium',
      description: 'ORS and IV fluid shortages possible',
      mitigation: 'Pre-positioning, emergency procurement, UNICEF coordination',
    },
    {
      risk: 'Community Resistance',
      severity: 'medium',
      description: 'Stigma and misinformation about treatment',
      mitigation: 'Community engagement, religious leader involvement, transparent communication',
    },
    {
      risk: 'Seasonal Flooding',
      severity: 'medium',
      description: 'Rains may worsen water contamination',
      mitigation: 'Accelerated water source protection, drainage improvements',
    },
  ],
  coordination: {
    clusters: [
      { name: 'WASH Cluster', role: 'Active member', activities: 'Emergency response coordination' },
      { name: 'Health Cluster', role: 'Observer', activities: 'Case management support' },
    ],
    unAgencies: [
      { name: 'WHO', collaboration: 'Technical guidance, case definition' },
      { name: 'UNICEF', collaboration: 'ORS supply, water treatment chemicals' },
      { name: 'OCHA', collaboration: 'Coordination, resource mobilization' },
    ],
    ingos: ['MSF', 'IRC', 'Concern', 'Oxfam'],
    localPartners: [
      { type: 'Regional Health Bureau', role: 'Technical coordination, case management' },
      { type: 'Water Bureau', role: 'Water source testing, rehabilitation' },
      { type: 'Community health workers', role: 'Surveillance, ORS distribution' },
    ],
    informationSharing: [
      'Daily case updates',
      'Weekly epidemiological bulletin',
      'Real-time water quality data',
    ],
  },
};

// Get situation data by country
export const getSituationData = (country: string): SituationData | null => {
  switch (country) {
    case 'Sudan':
      return sudanSituationData;
    case 'Ethiopia':
      return ethiopiaSituationData;
    default:
      return null;
  }
};

// Get response data by country
export const getResponseData = (country: string): ResponseData | null => {
  switch (country) {
    case 'Sudan':
      return sudanResponseData;
    case 'Ethiopia':
      return ethiopiaResponseData;
    default:
      return null;
  }
};

// Get available countries with full data
export const getCountriesWithData = (): string[] => {
  return ['Sudan', 'Ethiopia'];
};

// Get all NRC countries grouped by region
export const getNRCCountriesByRegion = (): Record<string, string[]> => {
  const grouped: Record<string, string[]> = {};
  nrcLocations
    .filter(loc => loc.region !== 'Representation Office' && loc.isActive)
    .forEach(loc => {
      if (!grouped[loc.region]) {
        grouped[loc.region] = [];
      }
      if (!grouped[loc.region].includes(loc.country)) {
        grouped[loc.region].push(loc.country);
      }
    });
  return grouped;
};

// Get all NRC countries as flat list
export const getAllNRCCountries = (): string[] => {
  return nrcLocations
    .filter(loc => loc.region !== 'Representation Office' && loc.isActive)
    .map(loc => loc.country)
    .sort();
};

// Get regions for a country
export const getRegionsForCountry = (country: string): string[] => {
  const countryRegions: Record<string, string[]> = {
    'Sudan': ['All Regions', 'Khartoum', 'North Darfur', 'South Darfur', 'West Darfur', 'Central Darfur', 'Blue Nile', 'Red Sea', 'Kassala'],
    'Ethiopia': ['All Regions', 'Somali', 'Oromia', 'Afar', 'Amhara', 'Tigray', 'SNNPR'],
    'South Sudan': ['All Regions', 'Central Equatoria', 'Jonglei', 'Unity', 'Upper Nile', 'Lakes'],
    'Somalia': ['All Regions', 'Banadir', 'Bay', 'Gedo', 'Lower Juba', 'Middle Shabelle'],
    'Yemen': ['All Regions', "Sana'a", 'Aden', 'Taiz', 'Hodeidah', 'Marib'],
  };
  return countryRegions[country] || ['All Regions'];
};
